import { Kbd, Text, TextInput, ThemeIcon, UnstyledButton } from '@mantine/core';
import { useHotkeys, useMediaQuery } from '@mantine/hooks';
import { IconSearch } from '@tabler/icons-react';
import classNames from 'classnames/bind';
import { i18nKeys, useTranslation } from 'locales';
import type { i18nKey } from 'locales/logic/types';
import {
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
  useEffect,
  useId,
  useState,
} from 'react';
import { chunk } from 'remeda';
import styleIdentifiers from './BatchActions.module.scss';

const styles = classNames.bind(styleIdentifiers);

export enum Categories {
  FOLLOW_UP = 'FOLLOW_UP',
  ASSIGNMENT = 'ASSIGNMENT',
  STATUS = 'STATUS',
  RECOVERY = 'RECOVERY',
  OTHER = 'OTHER',
}

export type BatchAction = {
  name: i18nKey;
  description: i18nKey;
  function: () => unknown;
  icon: ReactNode;
  category: Categories;
};

const SECTION_COLORS: Record<Categories, string> = {
  [Categories.RECOVERY]: 'purple',
  [Categories.STATUS]: 'green',
  [Categories.FOLLOW_UP]: 'orange',
  [Categories.ASSIGNMENT]: 'blue',
  [Categories.OTHER]: 'gray',
};

const foldForSearch = (text: string) =>
  text
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');

const neighbourInList = (actions: ReadonlyArray<BatchAction>, action: BatchAction, delta: number) =>
  actions[(actions.indexOf(action) + delta + actions.length) % actions.length];

const neighbourInRows = (
  rows: ReadonlyArray<ReadonlyArray<BatchAction>>,
  action: BatchAction,
  delta: number,
) => {
  const rowIndex = rows.findIndex(row => row.includes(action));
  const nextRow = rows[(rowIndex + delta + rows.length) % rows.length];

  return nextRow[Math.min(rows[rowIndex].indexOf(action), nextRow.length - 1)];
};

type BatchActionsListProps = {
  actions: ReadonlyArray<BatchAction>;
  searchFieldRef: RefObject<HTMLInputElement | null>;
};

export const BatchActionsList = ({ actions, searchFieldRef }: BatchActionsListProps) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeName, setActiveName] = useState<i18nKey | null>(null);
  const listId = useId();
  const isSingleColumn = useMediaQuery('(max-width: 560px)');

  const focusSearchField = () => searchFieldRef.current?.focus();
  useHotkeys([
    ['/', focusSearchField],
    ['shift+/', focusSearchField],
  ]);

  useEffect(() => {
    const dialog = searchFieldRef.current?.closest<HTMLElement>('[role="dialog"]');

    return () => {
      if (dialog?.contains(document.activeElement)) return;
      dialog?.focus();
    };
  }, [searchFieldRef]);

  const matchingActions = actions.filter(action =>
    foldForSearch(`${t(action.name)} ${t(action.description)}`).includes(
      foldForSearch(searchQuery),
    ),
  );

  const sections = Object.values(Categories)
    .map(category => ({
      category,
      actions: matchingActions.filter(action => action.category === category),
    }))
    .filter(section => section.actions.length > 0);

  const orderedActions = sections.flatMap(section => section.actions);
  const activeAction =
    orderedActions.find(action => action.name === activeName) ?? orderedActions[0];
  const optionId = (action: BatchAction) => `${listId}-${orderedActions.indexOf(action)}`;

  const rows = sections.flatMap(section => chunk(section.actions, isSingleColumn ? 1 : 2));

  const actionForKey = (key: string, current: BatchAction, isInSearch: boolean) => {
    if (key === 'ArrowDown') return neighbourInRows(rows, current, 1);
    if (key === 'ArrowUp') return neighbourInRows(rows, current, -1);
    if (isInSearch) return undefined;
    if (key === 'ArrowRight') return neighbourInList(orderedActions, current, 1);
    if (key === 'ArrowLeft') return neighbourInList(orderedActions, current, -1);
    return undefined;
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (!activeAction) return;

    if (event.key === 'Enter') {
      event.preventDefault();
      activeAction.function();
      return;
    }

    const isInSearch = event.target instanceof HTMLInputElement;
    const nextAction = actionForKey(event.key, activeAction, isInSearch);
    if (!nextAction) return;

    event.preventDefault();
    setActiveName(nextAction.name);
    const option = document.getElementById(optionId(nextAction));
    option?.scrollIntoView({ block: 'nearest' });
    if (!isInSearch) option?.focus();
  };

  return (
    <>
      <TextInput
        ref={searchFieldRef}
        data-autofocus
        autoFocus
        size="md"
        leftSection={<IconSearch stroke={1.5} size={18} aria-hidden />}
        rightSection={<Kbd size="xs">/</Kbd>}
        aria-label={t(i18nKeys.BATCH.SEARCH)}
        placeholder={t(i18nKeys.BATCH.SEARCH_PLACEHOLDER)}
        aria-controls={listId}
        aria-activedescendant={activeAction && optionId(activeAction)}
        value={searchQuery}
        onChange={event => {
          setSearchQuery(event.currentTarget.value);
          setActiveName(null);
        }}
        onKeyDown={handleKeyDown}
      />
      <div
        id={listId}
        role="listbox"
        className={styles('list')}
        tabIndex={-1}
        aria-label={t(i18nKeys.FORM.ACTIONS)}
        onKeyDown={handleKeyDown}
      >
        {sections.map(({ category, actions: sectionActions }) => (
          <div key={category} className={styles('section')}>
            <Text component="h5" size="xs" fw={600} c="dimmed" tt="uppercase" mb="xs">
              {t(i18nKeys.BATCH.CATEGORIES[category])} · {sectionActions.length}
            </Text>
            <div className={styles('grid')}>
              {sectionActions.map(action => (
                <UnstyledButton
                  key={t(action.name)}
                  id={optionId(action)}
                  role="option"
                  aria-selected={action === activeAction}
                  className={styles('action')}
                  onClick={action.function}
                  onMouseEnter={() => setActiveName(action.name)}
                  onFocus={() => setActiveName(action.name)}
                >
                  <ThemeIcon
                    variant="light"
                    color={SECTION_COLORS[category]}
                    size={36}
                    radius="md"
                    aria-hidden
                  >
                    {action.icon}
                  </ThemeIcon>
                  <span>
                    <Text size="sm" fw={600}>
                      {t(action.name)}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {t(action.description)}
                    </Text>
                  </span>
                </UnstyledButton>
              ))}
            </div>
          </div>
        ))}
      </div>
      {matchingActions.length === 0 && <Text mt="md">{t(i18nKeys.NO_RESULT_FOR_THIS_SEARCH)}</Text>}
    </>
  );
};
